import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api, web, TOKEN_KEY } from "../lib/api";

const AuthCtx = createContext(null);
const LEVEL_KEY = "gd_level";
/** Players without a level count as Varsity (the GAMEDAY app's default team). */
export const levelOf = (p) => (p?.level === "jv" ? "jv" : "varsity");

export function AuthProvider({ children }) {
  const [me, setMe] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [managerTeams, setManagerTeams] = useState([]);
  const [ready, setReady] = useState(false);
  const [teamId, setTeamIdState] = useState(localStorage.getItem("gd_team"));

  const loadMe = useCallback(async () => {
    try {
      const { data } = await api.get("/me");
      const flags = await web.get("/flags").catch(() => ({ data: {} }));
      setIsAdmin(!!flags.data?.is_admin);
      setManagerTeams(Array.isArray(flags.data?.manager_team_ids) ? flags.data.manager_team_ids : []);
      if (flags.data?.refresh) {
        const again = await api.get("/me");
        setMe(again.data);
        return again.data;
      }
      setMe(data);
      return data;
    } catch {
      localStorage.removeItem(TOKEN_KEY);
      setMe(null);
      return null;
    } finally {
      setReady(true);
    }
  }, []);

  useEffect(() => {
    if (localStorage.getItem(TOKEN_KEY)) loadMe();
    else setReady(true);
  }, [loadMe]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem(TOKEN_KEY, data.access_token);
    return loadMe();
  };

  const loginWithToken = async (token) => {
    localStorage.setItem(TOKEN_KEY, token);
    return loadMe();
  };

  const signup = async (kind, payload) => {
    const { data } = await api.post(kind === "recruiter" ? "/auth/recruiter-signup" : "/auth/signup", payload);
    localStorage.setItem(TOKEN_KEY, data.access_token);
    return loadMe();
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem("gd_team");
    setMe(null);
    setIsAdmin(false);
  };

  /** Team managers are coaches in the mobile app; on the website they only run schedule, attendance and messages. */
  const memberships = (Array.isArray(me?.memberships) ? me.memberships : [])
    .map((m) => (m.role === "coach" && managerTeams.includes(m.team_id) ? { ...m, web_role: "manager" } : m));
  const membership = memberships.find((m) => m.team_id === teamId) ?? memberships[0] ?? null;
  const setTeamId = (id) => { localStorage.setItem("gd_team", id); setTeamIdState(id); };
  const isManager = membership?.web_role === "manager";
  const canEdit = ["owner", "coach"].includes(membership?.role) && !isManager;
  const canRun = canEdit || isManager;

  const [level, setLevelState] = useState(localStorage.getItem(LEVEL_KEY) ?? "all");
  const setLevel = (v) => { localStorage.setItem(LEVEL_KEY, v); setLevelState(v); };
  const inLevel = useCallback((p) => level === "all" || levelOf(p) === level, [level]);

  return (
    <AuthCtx.Provider value={{ me, ready, login, loginWithToken, signup, refresh: loadMe, logout, memberships, membership, setTeamId, canEdit, canRun, isManager, isAdmin, level, setLevel, inLevel }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
